import React, { useRef, useState, useCallback, useEffect } from "react";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";
import { Button, Space, Tooltip } from "antd";
import { ZoomInOutlined, ZoomOutOutlined, ReloadOutlined } from "@ant-design/icons";
import useAnnotationEvents from '../hooks/useAnnotationEvents';
import AnnotationToolPicker from "./AnnotationToolPicker";
import AnnotationShapesSvg from "./AnnotationShapesSvg";
import {
  SHAPE_TO_TOOL_NAME,
  SHAPE_LABEL,
  getShapeKey,
  getGeometry,
  computeBboxGeometry,
  isMeaningfulGeometry,
} from "../utils/annotationShapes";

const MacroControls = ({ activeShape, isDrawing, onShapePick }) => {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div
      style={{
        position: "absolute",
        top: "15px",
        left: "15px",
        zIndex: 10,
      }}
    >
      <Space direction="vertical">
        <Tooltip title="Zoom avant" placement="right">
          <Button
            type="primary"
            shape="circle"
            size="middle"
            className="viewer-tool-btn"
            icon={<ZoomInOutlined />}
            onClick={() => zoomIn()}
          />
        </Tooltip>
        <Tooltip title="Zoom arrière" placement="right">
          <Button
            type="primary"
            shape="circle"
            size="middle"
            className="viewer-tool-btn"
            icon={<ZoomOutOutlined />}
            onClick={() => zoomOut()}
          />
        </Tooltip>
        <Tooltip title="Réinitialiser" placement="right">
          <Button
            type="primary"
            shape="circle"
            size="middle"
            className="viewer-tool-btn"
            icon={<ReloadOutlined />}
            onClick={() => resetTransform()}
          />
        </Tooltip>
        <AnnotationToolPicker
          activeShape={activeShape}
          isDrawing={isDrawing}
          onPick={onShapePick}
          placement="right"
          buttonClassName="viewer-tool-btn"
        />
      </Space>
    </div>
  );
};

const MacroViewer = ({ images, annotations = [], targetAnnotationId = null, showAnnotations = true, onAnnotationCompleted }) => {
  const transformRef = useRef(null);
  const wrapperRef = useRef(null);
  const imageRef = useRef(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [activeShape, setActiveShape] = useState("rectangle");
  const [isDrawing, setIsDrawing] = useState(false);
  const [preview, setPreview] = useState(null);
  const drawingStateRef = useRef({ start: null, points: [] });

  const cleanupPreview = useCallback(() => {
    setPreview(null);
    drawingStateRef.current = { start: null, points: [] };
  }, []);

  const cleanupOnExternalEvent = useCallback(() => {
    cleanupPreview();
    setIsDrawing(false);
  }, [cleanupPreview]);

  // Adapter for the existing useAnnotationEvents hook (it expects drawingRef.rectangle truthiness)
  const drawingCompatRef = useRef({ rectangle: null });
  useEffect(() => {
    drawingCompatRef.current.rectangle = preview ? { id: 'preview' } : null;
  }, [preview]);
  useAnnotationEvents('macro', drawingCompatRef, () => {}, cleanupOnExternalEvent);

  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';

  const getRelativePoint = (clientX, clientY) => {
    const imageElement = imageRef.current;
    if (!imageElement) return null;
    const rect = imageElement.getBoundingClientRect();
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x, y };
  };

  const getPixelDeltas = (start, current) => {
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect) return { dxPx: 0, dyPx: 0 };
    return {
      dxPx: (current.x - start.x) * rect.width,
      dyPx: (current.y - start.y) * rect.height,
    };
  };

  const computeCircleGeometry = (start, current) => {
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect) return computeBboxGeometry(start, current);
    const { dxPx, dyPx } = getPixelDeltas(start, current);
    const rPx = Math.max(Math.abs(dxPx), Math.abs(dyPx));
    const signX = dxPx >= 0 ? 1 : -1;
    const signY = dyPx >= 0 ? 1 : -1;
    const endRel = {
      x: start.x + (signX * rPx) / rect.width,
      y: start.y + (signY * rPx) / rect.height,
    };
    return computeBboxGeometry(start, endRel);
  };

  const computeGeometry = (shape, start, current) => {
    if (shape === "circle") return computeCircleGeometry(start, current);
    if (shape === "rectangle" || shape === "ellipse") return computeBboxGeometry(start, current);
    if (shape === "arrow") return { x1: start.x, y1: start.y, x2: current.x, y2: current.y };
    return null;
  };

  const handleSvgMouseDown = (e) => {
    if (!isDrawing) return;
    const p = getRelativePoint(e.clientX, e.clientY);
    if (!p) return;
    e.preventDefault();
    e.stopPropagation();
    if (activeShape === "freehand") {
      drawingStateRef.current.points = [[p.x, p.y]];
      setPreview({ shape: "freehand", geometry: { points: [[p.x, p.y]] } });
    } else {
      drawingStateRef.current.start = p;
      setPreview({ shape: activeShape, geometry: computeGeometry(activeShape, p, p) });
    }
  };

  const handleSvgMouseMove = (e) => {
    if (!isDrawing || !preview) return;
    const p = getRelativePoint(e.clientX, e.clientY);
    if (!p) return;
    e.preventDefault();
    e.stopPropagation();
    if (activeShape === "freehand") {
      const pts = drawingStateRef.current.points;
      const last = pts[pts.length - 1];
      if (!last || Math.hypot(p.x - last[0], p.y - last[1]) > 0.002) {
        pts.push([p.x, p.y]);
        setPreview({ shape: "freehand", geometry: { points: [...pts] } });
      }
    } else {
      const start = drawingStateRef.current.start;
      if (!start) return;
      setPreview({ shape: activeShape, geometry: computeGeometry(activeShape, start, p) });
    }
  };

  const handleSvgMouseUp = (e) => {
    if (!isDrawing || !preview) return;
    e.preventDefault();
    e.stopPropagation();
    const finalShape = preview.shape;
    const finalGeom = preview.geometry;
    if (!isMeaningfulGeometry(finalShape, finalGeom)) {
      cleanupPreview();
      setIsDrawing(false);
      return;
    }
    const toolName = SHAPE_TO_TOOL_NAME[finalShape];
    const annotationData = {
      bounds: finalShape === "rectangle"
        ? { x: finalGeom.x, y: finalGeom.y, width: finalGeom.width, height: finalGeom.height }
        : undefined,
      geometry: finalGeom,
      toolName,
      timestamp: new Date().toISOString(),
      description: "Added annotation",
      id: `macro-anno-${Date.now()}`,
      name: `${SHAPE_LABEL[finalShape]} ROI`,
    };
    if (typeof onAnnotationCompleted === "function") {
      onAnnotationCompleted("macro-initial", annotationData);
    }
    drawingStateRef.current = { start: null, points: [] };
  };

  const handleShapePick = (shapeKey) => {
    if (isDrawing && shapeKey === activeShape) {
      setIsDrawing(false);
      cleanupPreview();
      return;
    }
    setActiveShape(shapeKey);
    setIsDrawing(true);
    cleanupPreview();
  };

  const zoomToAnnotation = useCallback((annotationId) => {
    const anno = annotations.find(a => a.id === annotationId);
    const instance = transformRef.current;
    const targetElementId = `macro-anno-${annotationId}`;
    const targetElement = document.getElementById(targetElementId);

    if (anno && instance && targetElement && imageDimensions.width > 0 && imageDimensions.height > 0) {
      const geom = getGeometry(anno);
      if (!geom) return;

      let x, y, width, height;
      if (geom.points) {
        const xs = geom.points.map(p => p[0]);
        const ys = geom.points.map(p => p[1]);
        x = Math.min(...xs) * imageDimensions.width;
        y = Math.min(...ys) * imageDimensions.height;
        width = (Math.max(...xs) - Math.min(...xs)) * imageDimensions.width;
        height = (Math.max(...ys) - Math.min(...ys)) * imageDimensions.height;
      } else if (geom.x1 !== undefined) {
        x = Math.min(geom.x1, geom.x2) * imageDimensions.width;
        y = Math.min(geom.y1, geom.y2) * imageDimensions.height;
        width = Math.abs(geom.x2 - geom.x1) * imageDimensions.width;
        height = Math.abs(geom.y2 - geom.y1) * imageDimensions.height;
      } else {
        x = geom.x * imageDimensions.width;
        y = geom.y * imageDimensions.height;
        width = geom.width * imageDimensions.width;
        height = geom.height * imageDimensions.height;
      }

      const wrapperWidth = wrapperRef.current.clientWidth;
      const wrapperHeight = wrapperRef.current.clientHeight;
      const fitScale = Math.min(wrapperWidth / Math.max(width, 1), wrapperHeight / Math.max(height, 1));
      const targetScale = fitScale * 0.4;

      instance.zoomToElement(targetElement, targetScale, 200, 'easeOut');
    }
  }, [annotations, imageDimensions]);

  useEffect(() => {
    if (targetAnnotationId) {
      const timeoutId = setTimeout(() => {
        if (transformRef.current) zoomToAnnotation(targetAnnotationId);
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [targetAnnotationId, zoomToAnnotation]);

  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.target;
    setImageDimensions({ width: naturalWidth, height: naturalHeight });
    if (transformRef.current) transformRef.current.resetTransform(0);
  };

  // Fit the image inside the frame (letterboxed) by measuring the container and
  // sizing a display box to the image's aspect ratio. Annotation overlays are
  // positioned as fractions of this box, so alignment is preserved.
  const [box, setBox] = useState({ w: 0, h: 0 });
  const computeBox = useCallback(() => {
    const el = wrapperRef.current;
    const { width: nw, height: nh } = imageDimensions;
    if (!el || !nw || !nh) return;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    if (!cw || !ch) return;
    const s = Math.min(cw / nw, ch / nh);
    setBox({ w: Math.round(nw * s), h: Math.round(nh * s) });
  }, [imageDimensions]);

  useEffect(() => {
    computeBox();
  }, [computeBox]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => computeBox());
    ro.observe(el);
    return () => ro.disconnect();
  }, [computeBox]);

  // Build per-annotation invisible bbox div for zoomToElement targeting (needs DOM element)
  const annoBboxes = annotations.map(anno => {
    const geom = getGeometry(anno);
    if (!geom) return null;
    let x, y, width, height;
    if (geom.points) {
      const xs = geom.points.map(p => p[0]);
      const ys = geom.points.map(p => p[1]);
      x = Math.min(...xs); y = Math.min(...ys);
      width = Math.max(...xs) - x; height = Math.max(...ys) - y;
    } else if (geom.x1 !== undefined) {
      x = Math.min(geom.x1, geom.x2); y = Math.min(geom.y1, geom.y2);
      width = Math.abs(geom.x2 - geom.x1); height = Math.abs(geom.y2 - geom.y1);
    } else {
      x = geom.x; y = geom.y; width = geom.width; height = geom.height;
    }
    return { id: anno.id, x, y, width, height };
  }).filter(Boolean);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        backgroundColor: isDarkMode ? "#000000" : "#f5f5f5",
        overflow: "hidden",
        cursor: isDrawing ? 'crosshair' : 'grab'
      }}
    >
      <TransformWrapper
        initialScale={1}
        minScale={0.2}
        wheel={{ step: 0.1 }}
        doubleClick={{ disabled: true }}
        panning={{ disabled: isDrawing, lockAxisX: false, lockAxisY: false }}
        limitToBounds={false}
        centerOnInit
        centerZoomedOut
        ref={transformRef}
      >
        <MacroControls
          activeShape={activeShape}
          isDrawing={isDrawing}
          onShapePick={handleShapePick}
        />
        <TransformComponent
          wrapperStyle={{ position: 'relative', width: '100%', height: '100%' }}
          contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            style={{
              position: 'relative',
              width: box.w ? `${box.w}px` : 'auto',
              height: box.h ? `${box.h}px` : 'auto',
            }}
          >
            <img
              ref={imageRef}
              src={images[0]}
              alt="Macro Viewer"
              onLoad={handleImageLoad}
              style={{
                display: 'block',
                width: box.w ? '100%' : 'auto',
                height: box.h ? '100%' : 'auto',
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
            {/* Invisible bbox markers for zoomToElement */}
            {annoBboxes.map(bb => (
              <div
                key={bb.id}
                id={`macro-anno-${bb.id}`}
                style={{
                  position: 'absolute',
                  left: `${bb.x * 100}%`,
                  top: `${bb.y * 100}%`,
                  width: `${bb.width * 100}%`,
                  height: `${bb.height * 100}%`,
                  pointerEvents: 'none',
                  visibility: 'hidden',
                }}
              />
            ))}
            {showAnnotations && imageDimensions.width > 0 && (
              <AnnotationShapesSvg
                annotations={annotations}
                preview={preview}
                pointerEvents={isDrawing ? "auto" : "none"}
                interactive={!isDrawing}
                onMouseDown={handleSvgMouseDown}
                onMouseMove={handleSvgMouseMove}
                onMouseUp={handleSvgMouseUp}
                shapeOpts={{ strokeWidth: 2 }}
              />
            )}
          </div>
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
};

export default MacroViewer;
